import React from 'react';
import ActiveRepsList from 'components/coaching/landing-pages/ActiveRepsList';
import NoReps from 'components/coaching/landing-pages/helpers/NoReps';
import InactiveRepsTabPanel from 'components/coaching/landing-pages/InactiveRepsTabPanel';
import {List} from 'immutable';

/**
 * Container for main rep lists.
 */
export class RepLists extends React.PureComponent {
  /**
   * Does this manager have any reps that have active plans?
   *
   * @param coachingPlans
   * @returns {boolean}
   */
  haveActiveReps({coachingPlans}) {
    return coachingPlans.size > 0;
  }

  /**
   * Does this manager have any reps that don't have active plans?
   *
   * @param coachingPlans
   * @param directReports
   * @param managerId
   * @returns {boolean}
   */
  haveInactiveReps({coachingPlans, directReports, managerId}) {
    // TODO we should probably redefine direct reports to always remove the manager
    const directReportsMinusMe = directReports.filter(r => r.userId !== managerId);
    const repsWithoutPlans = directReportsMinusMe
      .map(rep => [rep, coachingPlans.filter(plan => plan.recipient.id === rep.userId)])
      .filter(([, plans]) => plans.size === 0);

    return repsWithoutPlans.size > 0;
  }

  /** Group plans into those coached by the manager and those coached by others. */
  groupPlans({coachingPlans}) {
    const {managerId, coachId} = this.props;
    // plans are those with either the user identified by managerId as the
    // coach or the manager. Thus, plans that don't have this user as the coach
    // must be members of this user's team coached by others.
    const plansCoachId = coachId ? coachId : managerId;
    const groupedPlans = coachingPlans.groupBy(
      plan =>
        plan.coach.id === plansCoachId ||
        plan.reviews.filter(r => r.status === 'SCHEDULED').some(r => r.coachUserId === plansCoachId)
    );
    return {
      manager: groupedPlans.get(true) || List(),
      other: groupedPlans.get(false) || List(),
    };
  }

  managerCoachedTableHeader(isManagerLoggedIn, manager) {
    if (!manager) {
      // Might not have fetched manager from the server yet.
      return '';
    }
    const whoIsCoaching = isManagerLoggedIn ? "You're" : `${manager.firstName} is`;
    return `Reps ${whoIsCoaching} Coaching`;
  }

  render() {
    const {isManagerLoggedIn, manager, showMoreLink} = this.props;
    const haveActiveReps = this.haveActiveReps(this.props);
    const haveInactiveReps = this.haveInactiveReps(this.props);
    const groupedPlans = this.groupPlans(this.props);
    const managerListProps = {
      ...this.props,
      showMoreLink,
      coachingPlans: groupedPlans.manager,
      tableHeader: this.managerCoachedTableHeader(isManagerLoggedIn, manager),
    };
    const otherListProps = {
      ...this.props,
      coachingPlans: groupedPlans.other,
      tableHeader: isManagerLoggedIn
        ? "Your Team's Plans With Other Coaches"
        : 'Coaching Plans With Other Coaches',
    };
    const forCoach = !!this.props.coachId;

    return (
      <div className="rep-lists-outer-container">
        {/** Active becomes "your team" if all reps have a plan and inactive doesn't show. **/}
        {haveActiveReps && groupedPlans.manager.size > 0 && (
          <ActiveRepsList
            {...managerListProps}
            mananger={manager}
            adminId={this.props.adminId}
            startDate={this.props.startDate}
            endDate={this.props.endDate}
            fromPath={this.props.fromPath}
            showFilterToggle={
              // suppress toggle if viewing from admin
              !this.props.suppressToggle
            }
          />
        )}
        {
          /* for coaches we don't show plans with other coaches */
          !forCoach && haveActiveReps && groupedPlans.other.size > 0 && (
            <ActiveRepsList
              {...otherListProps}
              manager={manager}
              adminId={this.props.adminId}
              coachId={this.props.coachId}
              startDate={this.props.startDate}
              endDate={this.props.endDate}
              fromPath={this.props.fromPath}
              showFilterToggle={false}
            />
          )
        }
        {haveInactiveReps && !forCoach && (
          <InactiveRepsTabPanel
            {...this.props}
            manager={manager}
            haveActiveReps={haveActiveReps}
            haveInactiveReps={haveInactiveReps}
          />
        )}
        {
          /* check forCoach because we don't show inactive reps for coaches */
          (forCoach || !haveInactiveReps) && !haveActiveReps && (
            <NoReps isManagerLoggedIn={isManagerLoggedIn} manager={manager} forCoach={forCoach} />
          )
        }
      </div>
    );
  }
}
